import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save, Eye, Upload, Search, Loader2, ExternalLink, FileText, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Btn, IconBtn, Pill, type ScoreBreakdownEntry } from './ui';
import { ScoreBadge } from './ScoreBadge';
import { OverflowMenu, type OverflowMenuProps } from './OverflowMenu';
import { FormatToolbar, type FormatCommand } from './FormatToolbar';
import { AiSuggestButton } from './rail/cards/AiSuggestButton';
import { IssueBadge } from './rail/cards/IssueBadge';
import type { IssueBadgeSummary } from '@/lib/issueSummary';

export interface BreadcrumbCrumb {
  label: string;
  href?: string;
  onClick?: () => void;
}

export interface TopBarProps {
  // Row 1 — breadcrumbs + status pill + score + ⋯ + Save / Preview / Publish to CMS
  breadcrumbs: BreadcrumbCrumb[];
  isDirty?: boolean;
  saving?: boolean;
  score?: number;
  scoreGrade?: string;
  scoreDescription?: string;
  scoreBreakdown?: ScoreBreakdownEntry[];
  onOpenQualityPanel?: () => void;

  onBack?: () => void;
  onSave?: () => void;
  onPreview?: () => void;
  onPublish?: () => void;
  publishLabel?: string;
  /** Disable the publish button (e.g. content not ready). */
  publishDisabled?: boolean;
  /** Publish in flight — shows a spinner and disables the button so fast
   *  repeat clicks can't fire duplicate publish POSTs. */
  publishLoading?: boolean;
  /** When set, renders a ghost "View" button (opens the live post) beside publish. */
  onViewPost?: () => void;

  /**
   * Always-visible "Open in Google Doc" action. Opens the stored doc when one
   * exists; otherwise the page handler generates it first via the export
   * endpoint — so the button renders regardless of whether a doc URL is
   * stored yet.
   */
  onOpenGoogleDoc?: () => void;
  /** Doc generation in flight — spinner + disabled so duplicate clicks can't
   *  fire parallel exports. */
  gdocLoading?: boolean;

  overflowProps?: OverflowMenuProps;

  // Row 2 — title + counts (focus keyword sits in Row 1 beside the score)
  title: string;
  onTitle: (value: string) => void;
  titlePlaceholder?: string;
  focusKw?: string;
  onFocusKwClick?: () => void;
  wordCount?: number;
  charCount?: number;

  // Row 3 — format toolbar
  onFormat?: (cmd: FormatCommand) => void;
  activeFormats?: Partial<Record<FormatCommand, boolean>>;
  onAskAI?: () => void;

  /** Optional rows appended after the format toolbar (DocSubToolbar, InsertBlockBar, etc.). */
  extraRows?: ReactNode;
  className?: string;

  // Per-field suggest — title (H1)
  /** Content ID for the per-field suggest endpoint. */
  suggestContentId?: string;
  /** Tenant ID for the per-field suggest endpoint. */
  suggestTenantId?: string;
  /** Disable suggest while whole-run generation is in flight. */
  metadataGenerating?: boolean;

  /**
   * Unified issue summary for the badge next to Publish. When omitted, no
   * badge is rendered. When provided with total===0 a calm "Issues clear"
   * pill is shown.
   */
  issueSummary?: IssueBadgeSummary;
  /**
   * Fired when the reviewer clicks the issue badge. The page implements a
   * cursor that advances through the flagged fields, jumping the rail to each.
   */
  onIssuesClick?: () => void;
}

/**
 * The top chrome of the editor — three required rows plus an `extraRows` slot
 * for the optional Doc sub-toolbar (row 4) and Insert block bar (row 5).
 *
 *   Row 1  | breadcrumbs · Unsaved · Score · ⋯ · Save · Preview · Publish to CMS
 *   Row 2  | title input · focus-keyword pill · word/char counter
 *   Row 3  | format toolbar (undo/redo · headings · marks · lists · inserts) · Ask AI
 *   extras | DocSubToolbar (Ver / CMS target / Rules) + InsertBlockBar
 */
export function TopBar({
  breadcrumbs,
  isDirty,
  saving,
  score,
  scoreGrade,
  scoreDescription,
  scoreBreakdown,
  onOpenQualityPanel,
  onBack,
  onSave,
  onPreview,
  onPublish,
  publishLabel = 'Publish to CMS',
  onViewPost,
  onOpenGoogleDoc,
  gdocLoading,
  publishDisabled,
  publishLoading,
  overflowProps,
  title,
  onTitle,
  titlePlaceholder = 'Untitled article',
  // Focus keyword is surfaced next to the title (WordPress-like) — see Row 2.
  focusKw,
  onFocusKwClick,
  wordCount,
  charCount,
  onFormat,
  activeFormats,
  onAskAI,
  extraRows,
  className,
  suggestContentId,
  suggestTenantId,
  metadataGenerating,
  issueSummary,
  onIssuesClick,
}: TopBarProps) {
  return (
    <div className={cn('relative bg-card text-foreground', className)} data-component="top-bar">
      {/* Row 1 — breadcrumbs / status / score / actions */}
      <div className="flex items-center gap-2 px-3.5 py-1.5 min-h-11 border-b border-border">
        {onBack && (
          <IconBtn icon={<ArrowLeft className="size-4" />} label="Back" size={28} onClick={onBack} />
        )}
        <nav className="flex items-center gap-1.5 min-w-0 text-[12.5px]">
          {breadcrumbs.map((c, i) => {
            const isLast = i === breadcrumbs.length - 1;
            return (
              <div key={`${c.label}-${i}`} className="flex items-center gap-1.5 min-w-0">
                {i > 0 && <span className="text-muted-foreground/50">/</span>}
                {c.href ? (
                  // Router Link, NOT a raw <a>: a bare anchor triggers a full
                  // document load, which tears down the SPA (and any list
                  // state/query params the user came from, e.g. ?page=3).
                  <Link
                    to={c.href}
                    onClick={c.onClick}
                    className={cn(
                      'truncate no-underline',
                      isLast
                        ? 'text-foreground font-semibold max-w-[280px]'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {c.label}
                  </Link>
                ) : c.onClick ? (
                  <button
                    type="button"
                    onClick={c.onClick}
                    className={cn(
                      'truncate cursor-pointer bg-transparent border-none p-0',
                      isLast
                        ? 'text-foreground font-semibold max-w-[280px]'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {c.label}
                  </button>
                ) : (
                  <span
                    className={cn(
                      'truncate',
                      isLast
                        ? 'text-foreground font-semibold max-w-[280px]'
                        : 'text-muted-foreground',
                    )}
                  >
                    {c.label}
                  </span>
                )}
              </div>
            );
          })}
        </nav>

        {saving ? (
          <Pill tone="neutral" size="sm">
            Saving…
          </Pill>
        ) : isDirty ? (
          <Pill tone="warning" size="sm">
            Unsaved
          </Pill>
        ) : (
          <Pill tone="success" size="sm">
            Saved
          </Pill>
        )}

        <div className="flex-1" />

        {/* Focus keyword — centered in the top bar: a flex spacer on either
            side parks it midway between the breadcrumbs and the score/actions
            cluster. Value + handler arrive via DocChrome. */}
        {focusKw && (
          <Pill
            tone="coral"
            size="md"
            icon={<Search className="size-3" />}
            onClick={onFocusKwClick}
            ariaLabel="Focus keyword"
            title={focusKw}
            // Grow with the available center space (the flex-1 spacers absorb
            // the rest) instead of a hard 220px cap; shrink on narrow screens.
            // `truncate` must sit on the TEXT node, not this flex container —
            // on the container the text clips mid-word with no ellipsis.
            className="shrink min-w-0 max-w-[min(560px,36vw)]"
          >
            <span className="truncate">{focusKw}</span>
          </Pill>
        )}

        <div className="flex-1" />

        {typeof score === 'number' && (
          <ScoreBadge
            score={score}
            grade={scoreGrade}
            description={scoreDescription}
            breakdown={scoreBreakdown}
            onOpenFullPanel={onOpenQualityPanel}
          />
        )}

        <div className="mx-1 h-5 w-px bg-border" />

        <OverflowMenu {...overflowProps} />
        {onSave && (
          <Btn
            variant="ghost"
            size="sm"
            icon={<Save className="size-3.5" />}
            onClick={onSave}
            title="Saves your edits in the app — use “Publish to CMS” to push to WordPress"
          >
            Save changes
          </Btn>
        )}
        {onPreview && (
          <Btn variant="ghost" size="sm" icon={<Eye className="size-3.5" />} onClick={onPreview}>
            Preview
          </Btn>
        )}
        {onOpenGoogleDoc && (
          <Btn
            variant="ghost"
            size="sm"
            icon={
              gdocLoading ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <FileText className="size-3.5" />
              )
            }
            onClick={gdocLoading ? undefined : onOpenGoogleDoc}
            disabled={gdocLoading}
            title="Opens the formatted Google Doc — generates it first if this article has never been exported"
          >
            {gdocLoading ? 'Parsing to doc…' : 'Open in Google Doc'}
          </Btn>
        )}
        {onViewPost && (
          <Btn
            variant="ghost"
            size="sm"
            icon={<ExternalLink className="size-3.5" />}
            onClick={onViewPost}
          >
            View
          </Btn>
        )}
        {issueSummary && <IssueBadge summary={issueSummary} onClick={onIssuesClick} />}
        {onPublish && (
          <Btn
            variant="primary"
            size="sm"
            icon={
              publishLoading ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Upload className="size-3.5" />
              )
            }
            onClick={onPublish}
            disabled={publishDisabled || publishLoading}
            aria-busy={publishLoading || undefined}
          >
            {publishLabel}
          </Btn>
        )}
      </div>

      {/* Row 2 — title input + counters */}
      <div className="flex items-center gap-2.5 px-3.5 py-1.5 border-b border-border">
        <label
          className={cn(
            'group flex flex-1 min-w-0 items-center gap-1.5 -mx-1.5 px-1.5 py-0.5 rounded-md',
            'cursor-text transition-colors hover:bg-muted/40',
            'focus-within:bg-muted/40 focus-within:ring-1 focus-within:ring-coral/40',
          )}
          title="Click to edit the article title"
        >
          {/* AI-suggest docked on the LEFT of the title field, inside its
              hover/focus box so it reads as part of the field. */}
          {suggestContentId && suggestTenantId && (
            <span className="shrink-0" onClick={(e) => e.stopPropagation()}>
              <AiSuggestButton
                field="title"
                onSuggest={onTitle}
                currentValue={title}
                contentId={suggestContentId}
                tenantId={suggestTenantId}
                disabled={metadataGenerating}
                prominent
              />
            </span>
          )}
          <Pencil
            aria-hidden
            className="size-4 shrink-0 text-coral/70 transition-colors group-hover:text-coral group-focus-within:text-coral"
          />
          <input
            value={title}
            onChange={(e) => onTitle(e.target.value)}
            placeholder={titlePlaceholder}
            aria-label="Article title"
            className="flex-1 min-w-0 bg-transparent border-none outline-none px-0 py-0.5 text-[18px] font-bold text-foreground placeholder:text-muted-foreground/60"
          />
        </label>

        {(wordCount !== undefined || charCount !== undefined) && (
          <span className="font-mono text-[11px] text-muted-foreground min-w-[100px] text-right shrink-0">
            {wordCount !== undefined ? `${wordCount.toLocaleString()} words` : ''}
            {wordCount !== undefined && charCount !== undefined ? ' · ' : ''}
            {charCount !== undefined ? `${charCount.toLocaleString()} ch` : ''}
          </span>
        )}
      </div>

      {/* Row 3 — format toolbar */}
      <FormatToolbar onFormat={onFormat} activeFormats={activeFormats} onAskAI={onAskAI} />

      {extraRows}
    </div>
  );
}
